import React, { useMemo } from "react";
import * as yup from "yup";

import { Grid } from "@mui/material";
import { Input } from "Components/Form";
import { DespatchTask } from "Models/despatch.task";
import { Task, TaskDateProperties } from "Models/task";
import { useTranslation } from "react-i18next";
import { UseState } from "Types";
import { formatSimpleTime } from "Util/formatDateAndTime";
import useYupValidation from "Hooks/useYupValidation";
import { useAppState } from "Context/StateContext";

interface ChangeTaskTimeDefaultPeriodFormProps {
  task: DespatchTask | Task;
  errors?: yup.ValidationError[];
  setTask: UseState<DespatchTask | Task | undefined>;
  isLoading: boolean;
  showTimeFields?: boolean;

  setWhatHasChanged?: (field: string) => void;
}

export const ChangeTaskTimeMultiDayForm = ({
  task,
  isLoading,
  setTask,
  showTimeFields = false,
  setWhatHasChanged,
  errors = [],
}: ChangeTaskTimeDefaultPeriodFormProps) => {
  const { t: translate } = useTranslation();
  const { fieldHasError, getErrorByField } = useYupValidation(errors);
  const { formatWithUserTimezone } = useAppState();

  const startTime = useMemo(
    () => (task.startDate ? formatSimpleTime(task.startDate, { isUTC: true }) : null),
    [task.startDate]
  );

  const endTime = useMemo(
    () => (task.endDate ? formatSimpleTime(task.endDate, { isUTC: true }) : null),
    [task.endDate]
  );

  const getTimeChanged = (oldDate: Date, value: string) => {
    let currentTime = value;
    if (!value) currentTime = "23";

    const date = formatWithUserTimezone(oldDate, "yyyy-MM-dd");
    const dateString = `${date}T${currentTime}:00`;

    const newDate = new Date(dateString);

    return newDate;
  };

  const handleChangeTime = (value: string, key: TaskDateProperties) => {
    const baseDate = task.startDate ?? new Date();
    const oldDate = new Date(task[key] ?? baseDate);
    const newDate = getTimeChanged(oldDate, value);
    const newDateIso = newDate.toISOString();

    setTask(
      (state) =>
        ({
          ...state,
          [key]: newDateIso,
        } as DespatchTask)
    );
  };

  const handleChangeDateTime = (dateString: string, param: TaskDateProperties) => {
    const oldDate = new Date(task[param] ?? task.startDate);
    const time = formatWithUserTimezone(oldDate, "HH:mm");
    const date = new Date(`${dateString}T${time}:00`);

    const startDateIso = date.toISOString();

    setTask(
      (state) =>
        ({
          ...state,
          [param]: startDateIso,
        } as DespatchTask)
    );
  };

  return (
    <>
      <Grid item xs={12} sm={6}>
        <Input
          type="date"
          label={translate("startDate")}
          disabled={isLoading}
          disablePast
          min={new Date()}
          value={formatWithUserTimezone(task.startDate, "yyyy-MM-dd")}
          onChange={(event) => {
            handleChangeDateTime(event.target.value, "startDate");
            setWhatHasChanged?.("startDate");
          }}
          error={fieldHasError("startDate")}
          errorMessage={getErrorByField("startDate")}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <Input
          type="date"
          label={translate("endDate")}
          disabled={isLoading}
          disablePast
          min={task.startDate ?? new Date()}
          value={formatWithUserTimezone(task.endDate ?? task.startDate, "yyyy-MM-dd")}
          onChange={(event) => {
            handleChangeDateTime(event.target.value, "endDate");
            setWhatHasChanged?.("endDate");
          }}
          error={fieldHasError("endDate")}
          errorMessage={getErrorByField("endDate")}
        />
      </Grid>
      {showTimeFields && (
        <>
          <Grid item xs={6}>
            <Input
              type="time"
              label={translate("from")}
              value={startTime}
              onChange={(event) => handleChangeTime(event.target.value, "startDate")}
              error={fieldHasError("startTime")}
              errorMessage={getErrorByField("startTime")}
            />
          </Grid>
          <Grid item xs={6}>
            <Input
              type="time"
              label={translate("to")}
              value={endTime}
              onChange={(event) => handleChangeTime(event.target.value, "endDate")}
              error={fieldHasError("endTime")}
              errorMessage={getErrorByField("endTime")}
            />
          </Grid>
        </>
      )}
    </>
  );
};
